import 'App.scss';
import React, {
  Suspense, useEffect, useLayoutEffect, useMemo, useState,
} from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Provider } from 'react-redux';
import {
  BrowserRouter, Route, Switch, useHistory,
} from 'react-router-dom';

import ChatPlugin from 'ChatPlugin';
import MainModals from 'MainModals';
import Loading from 'components/atoms/Loading';
import Errors from 'components/templates/Errors';
import MainLayoutContainer from 'container/Mainlayout';
import useInitializeRender from 'hooks/useInitializeRender';
import useInitializeRoutes from 'hooks/useInitializeRoutes';
import HomeNavigate from 'navigations/homeNavigate';
import { Redirect301 } from 'navigations/redirectNavigate';
import LoginSso from 'pages/LoginSso';
import { store } from 'store';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import { toggleLayout } from 'store/layout';
import SocketConnection from 'utils/SocketConnection';
import { getStaticURL } from 'utils/language';

const AcLayoutContainer = React.lazy(() => import('container/AnalyticsCenter'));
const PageNavigate = React.lazy(() => import('navigations/pageNavigate'));
const AcAnalyticSimpleReportPage = React.lazy(() => import('pages/AcDashboard/AcAnalyticSimpleReport'));
const Ekyc = React.lazy(() => import('pages/Ekyc'));
const PreviewReport = React.lazy(() => import('pages/PreviewReport'));
const RecruitmentAuth = React.lazy(() => import('pages/RecruitmentAuth'));
const RecruitmentChangePassword = React.lazy(() => import('pages/RecruitmentChangePassword'));
const RecruitmentForgot = React.lazy(() => import('pages/RecruitmentForgot'));
const RecruitmentVerifyOtp = React.lazy(() => import('pages/RecruitmentVerifyOtp'));
const SignIn = React.lazy(() => import('pages/SignIn'));

interface RecruitmentWrapperProps {
  isAuthen: boolean;
}

const GoogleReCaptchaWrapper: React.FC = ({ children }) => {
  const [key, setKey] = useState<string>();
  const systemData = useAppSelector((state) => state.system.dataSystem);

  useEffect(() => {
    setTimeout(() => {
      if (systemData?.googleRecaptchaSiteKey) {
        setKey(systemData?.googleRecaptchaSiteKey);
      }
    }, 8000);
  }, [systemData]);

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={key}
      scriptProps={{
        async: true,
        defer: true,
        appendTo: 'head',
      }}
    >
      {children}
    </GoogleReCaptchaProvider>
  );
};

const queryClient = new QueryClient();

const MainlayoutWrapper: React.FC = ({ children }) => {
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(toggleLayout('main-layout'));
  }, [dispatch]);
  return <>{children}</>;
};

const RecruitmentWrapper: React.FC<RecruitmentWrapperProps> = ({
  children,
  isAuthen,
}) => {
  const dispatch = useAppDispatch();
  const { language, staticPages } = useAppSelector((state) => state.system);
  const { tokenRecruitment } = useAppSelector((state) => state.recruitment);
  const { push } = useHistory();
  useEffect(() => {
    if (isAuthen) {
      if (!tokenRecruitment) {
        push(`${getStaticURL('IDENTITY_VERIFICATION', language)}`, {
          redirectUrl: window.location.pathname,
        });
        return;
      }
    }
    dispatch(toggleLayout('recruit-layout'));
  }, [dispatch, push, language, staticPages, isAuthen, tokenRecruitment]);
  return <>{children}</>;
};

const App: React.FC = () => {
  const [error503, setError503] = useState(false);
  useInitializeRender();
  const {
    routesList,
    acRoutes,
    mainLayoutRouteList,
    recruitmentDetailsList,
  } = useInitializeRoutes();

  const { dashboard, acPages } = useMemo(() => {
    if (acRoutes && acRoutes.length > 0) {
      return {
        dashboard: acRoutes[0],
        acPages: acRoutes.slice(1),
      };
    }
    return {
      dashboard: undefined,
      acPages: undefined,
    };
  }, [acRoutes]);

  useLayoutEffect(() => {
    const handleCatchError503 = (e: any) => {
      setError503(e?.detail);
    };
    window.addEventListener('error503', handleCatchError503);
    return () => {
      window.removeEventListener('error503', handleCatchError503);
    };
  }, []);

  if (error503) {
    return (
      <Suspense fallback>
        <Switch>
          <Route path="*" render={() => <Errors statusCode={503} />} />
        </Switch>
      </Suspense>
    );
  }

  if (
    !routesList
    || !dashboard
    || !acPages
    || !mainLayoutRouteList
    || !recruitmentDetailsList
  ) {
    return <Loading variant="fullScreen" isFill isShow />;
  }

  return (
    <Suspense fallback={null}>
      <Switch>
        <Route
          path={routesList.recruitVerifyOtp}
          exact
          component={RecruitmentVerifyOtp}
        />
        <Route path={routesList.login} exact component={SignIn} />
        <Route
          path={routesList.changePassword}
          exact
          component={RecruitmentChangePassword}
        />
        <Route
          path={routesList.forgotPassword}
          exact
          component={RecruitmentForgot}
        />
        <Route
          exact
          path={['/login-sso', '/en/login-sso']}
          component={LoginSso}
        />
        <Route
          path={routesList.identityVerification}
          exact
          component={RecruitmentAuth}
        />
        <Route exact path={routesList.analysisReportPreview} component={PreviewReport} />
        <Route
          exact
          path={[
            '/trung-tam-phan-tich/bao-cao-chi-tiet',
            '/en/analysis-center/detail-report',
          ]}
          component={AcAnalyticSimpleReportPage}
        />
        <Route exact path={dashboard.route} component={dashboard.component} />
        <Route
          exact
          path={['/trung-tam-phan-tich/:slug', '/en/analysis-center/:slug']}
        >
          <AcLayoutContainer>
            <Switch>
              {acPages.map((item, index) => (
                <Route
                  path={item.route}
                  key={`acpages-${index.toString()}`}
                  component={item.component}
                />
              ))}
              <Route exact path={['/trung-tam-phan-tich/*', '/en/analysis-center/*']} component={Redirect301} />
            </Switch>
          </AcLayoutContainer>
        </Route>
        <Route>
          <MainLayoutContainer>
            <Switch>
              <Route
                path={[...routesList.home]}
                exact
                component={HomeNavigate}
              />
              {(Object.keys(mainLayoutRouteList) as Array<
                  keyof typeof mainLayoutRouteList
                >).map((ele) => mainLayoutRouteList[ele].route.map((item, index) => (
                  <Route
                    exact
                    key={`router-${ele}-${index.toString()}`}
                    path={item}
                    render={() => (
                      <MainlayoutWrapper>
                        {mainLayoutRouteList[ele].component}
                      </MainlayoutWrapper>
                    )}
                  />
              )))}
              {(Object.keys(recruitmentDetailsList) as Array<
                  keyof typeof recruitmentDetailsList
                >).map((ele) => recruitmentDetailsList[ele].route.map((item, index) => (
                  <Route
                    exact
                    key={`router-${ele}-${index.toString()}`}
                    path={item}
                    render={() => (
                      <RecruitmentWrapper
                        isAuthen={recruitmentDetailsList[ele].isAuthen}
                      >
                        {recruitmentDetailsList[ele].component}
                      </RecruitmentWrapper>
                    )}
                  />
              )))}
              <Route
                path={['/ekyc', '/en/ekyc']}
                exact
                component={Ekyc}
              />
              <Route
                exact
                path={[...routesList.pages]}
                component={PageNavigate}
              />
              <Route path={'/*'} component={Redirect301} />
            </Switch>
          </MainLayoutContainer>
        </Route>
      </Switch>
      <MainModals />
      <ChatPlugin />
    </Suspense>
  );
};

const AppWrapper: React.FC = () => (
  <Provider store={store}>
    <QueryClientProvider client={queryClient}>
      <GoogleReCaptchaWrapper>
        <BrowserRouter>
          <SocketConnection />
          <HelmetProvider>
            <App />
          </HelmetProvider>
        </BrowserRouter>
      </GoogleReCaptchaWrapper>
    </QueryClientProvider>
  </Provider>
);

export default AppWrapper;
